import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { LandingVariant, CreateLandingVariant, UpdateLandingVariant } from '../../../../shared/types/landingVariant';

/**
 * Public: fetch a landing variant by slug (no auth required).
 * Pass null to skip fetching.
 */
export function useLandingVariant(slug: string | null) {
  return useQuery({
    queryKey: ['landing-variant', slug],
    queryFn: async (): Promise<{ success: boolean; data?: LandingVariant }> => {
      return api.get(`/landing/${slug}`);
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !!slug,
  });
}

/**
 * Admin: list all landing variants
 */
export function useAdminLandingVariants() {
  return useQuery({
    queryKey: ['admin', 'landing-variants'],
    queryFn: async (): Promise<{ success: boolean; data?: LandingVariant[] }> => {
      return api.get('/admin/landing-variants');
    },
  });
}

/**
 * Admin: create a landing variant
 */
export function useCreateLandingVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLandingVariant): Promise<{ success: boolean; data?: LandingVariant }> => {
      return api.post('/admin/landing-variants', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'landing-variants'] });
    },
  });
}

/**
 * Admin: update a landing variant
 */
export function useUpdateLandingVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateLandingVariant & { id: string }): Promise<{ success: boolean; data?: LandingVariant }> => {
      return api.patch(`/admin/landing-variants/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'landing-variants'] });
    },
  });
}

/**
 * Admin: delete a landing variant
 */
export function useDeleteLandingVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; data?: LandingVariant }> => {
      return api.delete(`/admin/landing-variants/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'landing-variants'] });
    },
  });
}
